import { getIdentityToken } from "@privy-io/react-auth"
import { wrap } from "@reatom/core"
import { decodeJwt } from "jose"

export type GachaIdentitySession = {
  userId: string
  wallets: string[]
  identityToken: string | null
}

export type GachaIdentityScope = { userId?: string; walletAddress?: string }

// One cache for the gacha Privy session, shared by polling and user actions.
export const gachaIdentityTokens = createGachaIdentityTokenProvider({
  refresh: getIdentityToken,
})
export const getGachaIdentityToken = gachaIdentityTokens.getToken

export function createGachaIdentityTokenProvider({
  refresh,
  now = Date.now,
}: {
  refresh: () => Promise<string | null>
  now?: () => number
}) {
  let session: GachaIdentitySession | null = null
  let sessionKey: string | null = null
  let generation = 0
  let hasHadSession = false
  let cachedToken: string | null = null
  let cachedUntil = 0
  let inFlight: Promise<string | null> | null = null
  let retryAt = 0
  let refreshError: Error | null = null
  const remember = (token: string | null) => {
    cachedToken = token
    cachedUntil = now() + 60_000
  }
  const usable = (token: string | null) => {
    if (!token || !session) return false
    try {
      const claims = decodeJwt(token)
      return (
        claims.sub === session.userId &&
        typeof claims.exp === "number" &&
        claims.exp * 1000 > now() + 30_000
      )
    } catch {
      return false
    }
  }
  return {
    setSession: (next: GachaIdentitySession | null) => {
      const previousToken = session?.identityToken
      const nextKey = next
        ? JSON.stringify([
            next.userId,
            [
              ...new Set(next.wallets.map((wallet) => wallet.toLowerCase())),
            ].sort(),
          ])
        : null
      // After logout, require a fresh SDK read even for the same user. Do not
      // retain their previous credential just to compare it on the next login.
      const canUseSdkToken = !hasHadSession || session !== null
      if (nextKey !== sessionKey) {
        generation++
        cachedToken = null
        cachedUntil = 0
        inFlight = null
        refreshError = null
        retryAt = 0
      }
      sessionKey = nextKey
      session = next
      if (next) hasHadSession = true
      if (
        canUseSdkToken &&
        next?.identityToken !== previousToken &&
        usable(next?.identityToken ?? null)
      )
        remember(next?.identityToken ?? null)
    },
    getToken: async (scope: GachaIdentityScope = {}) => {
      if (
        !session ||
        (scope.userId !== undefined && scope.userId !== session.userId) ||
        (scope.walletAddress !== undefined &&
          !session.wallets.some(
            (wallet) =>
              wallet.toLowerCase() === scope.walletAddress?.toLowerCase(),
          ))
      )
        return null
      if (usable(cachedToken) && now() < cachedUntil) return cachedToken
      if (now() < retryAt) {
        if (refreshError) throw refreshError
        return null
      }
      if (inFlight) return await wrap(inFlight)
      const startedGeneration = generation
      const pending = (async () => {
        const token = await wrap(refresh())
        if (generation !== startedGeneration) return null
        refreshError = null
        if (!usable(token)) {
          retryAt = now() + 5_000
          return null
        }
        retryAt = 0
        remember(token)
        return token
      })()
      inFlight = pending
      try {
        return await wrap(pending)
      } catch (error) {
        if (generation !== startedGeneration) return null
        refreshError =
          error instanceof Error
            ? error
            : new Error("Identity refresh unavailable")
        const rateLimited =
          (typeof error === "object" &&
            error !== null &&
            (("status" in error && error.status === 429) ||
              ("privyErrorCode" in error &&
                error.privyErrorCode === "too_many_requests"))) ||
          /429|too.many.requests|rate.limit/i.test(refreshError.message)
        retryAt = now() + (rateLimited ? 60_000 : 5_000)
        throw refreshError
      } finally {
        if (inFlight === pending) inFlight = null
      }
    },
  }
}
